import { type ReactNode } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Typography,
} from '@mui/material'
import { Close as CloseIcon } from '@mui/icons-material'
import { themeColors } from '../../app/layout/theme'
import { FormInput, FormSelect } from '../ui'

/**
 * Common styling for form inputs within drawers to maintain consistent
 * layout and typography.
 */
export const entityDrawerInputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    bgcolor: '#fff',
    '& fieldset': { borderColor: themeColors.border },
    '&:hover fieldset': { borderColor: themeColors.primary },
    '&.Mui-focused fieldset': { borderColor: themeColors.primary, borderWidth: '1px' },
  },
  '& .MuiInputBase-input': {
    fontSize: '13px',
    color: themeColors.text.primary,
    '&::placeholder': { color: themeColors.text.muted, opacity: 1 },
  },
}

export type FieldType =
  | 'text' | 'name' | 'phone' | 'email' | 'password' 
  | 'pincode' | 'pan' | 'aadhar' | 'gstin' | 'ifsc' 
  | 'url' | 'number' | 'decimal'

interface EntityFormDrawerProps {
  open: boolean
  title: string
  icon?: ReactNode
  onClose: () => void
  onSubmit: () => void
  submitLabel: string
  isSubmitting?: boolean
  submitDisabled?: boolean
  cancelLabel?: string
  width?: number
  children: ReactNode
}

export function EntityFormDrawer({
  open,
  title,
  icon,
  onClose,
  onSubmit,
  submitLabel,
  isSubmitting = false,
  submitDisabled = false,
  cancelLabel = 'Cancel',
  width = 512,
  children,
}: EntityFormDrawerProps) {
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100vw', sm: width },
          maxWidth: '100vw',
          bgcolor: '#fff',
          boxShadow: themeColors.shadow?.dialog || '0 8px 32px rgba(0,0,0,0.12)',
        },
      }}
      ModalProps={{ keepMounted: true }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            height: 56,
            px: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
            {icon && (
              <Box sx={{ display: 'flex', color: themeColors.primary, flexShrink: 0 }}>
                {icon}
              </Box>
            )}
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: themeColors.text.primary }} noWrap>
              {title}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" disabled={isSubmitting}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        <Divider />

        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            px: 2,
            py: 2,
            pb: 10,
          }}
        >
          {children}
        </Box>

        <Box
          sx={{
            px: 2,
            py: 2,
            borderTop: `1px solid ${themeColors.border}`,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
            bgcolor: '#fff',
            flexShrink: 0,
          }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={isSubmitting}
            sx={{
              minWidth: 72,
              borderRadius: '8px',
              textTransform: 'none',
              borderColor: themeColors.primary,
              color: themeColors.primary,
              fontWeight: 500,
            }}
          >
            {cancelLabel}
          </Button>
          <Button
            variant="contained"
            onClick={onSubmit}
            disabled={isSubmitting || submitDisabled}
            sx={{
              minWidth: 102,
              borderRadius: '8px',
              textTransform: 'none',
              bgcolor: themeColors.primary,
              boxShadow: 'none',
              fontWeight: 700,
              '&:hover': { bgcolor: themeColors.primaryDark, boxShadow: 'none' },
            }}
          >
            {isSubmitting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : submitLabel}
          </Button>
        </Box>
      </Box>
    </Drawer>
  )
}

interface EntityFormSectionProps {
  title?: string
  children: ReactNode
  columns?: 1 | 2
}

export function EntityFormSection({ title, children, columns = 2 }: EntityFormSectionProps) {
  return (
    <Box sx={{ mb: 2.5 }}>
      {title && (
        <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1.5, color: themeColors.text.primary }}>
          {title}
        </Typography>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: columns === 1 ? '1fr' : '1fr 1fr' },
          columnGap: 2,
          rowGap: 1.75,
          width: '100%',
          justifyItems: 'stretch',
        }}
      >
        {children}
      </Box>
    </Box>
  )
}

export interface EntityDrawerSelectOption {
  label: string
  value: string
}

interface EntityDrawerInputProps {
  label?: string
  required?: boolean
  value: string
  onChange: (value: string) => void
  placeholder?: string
  error?: string
  helperText?: string
  fieldType?: FieldType
  type?: string
  multiline?: boolean
  minRows?: number
  rows?: number
  fullWidth?: boolean
  onBlur?: (e: any) => void
  inputProps?: any
  disabled?: boolean
  sx?: any
  InputProps?: any
}

export function EntityDrawerInput({
  label,
  required,
  value,
  onChange,
  onBlur,
  placeholder,
  error,
  helperText,
  fieldType = 'text',
  type,
  multiline,
  minRows,
  rows,
  fullWidth,
  inputProps,
  disabled,
  sx,
  InputProps,
}: EntityDrawerInputProps) {
  return (
    <FormInput
      label={label}
      labelPosition="above"
      required={required}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      placeholder={placeholder}
      error={!!error}
      helperText={error || helperText}
      fieldType={fieldType as any}
      type={type}
      multiline={multiline}
      minRows={minRows}
      rows={rows}
      fullWidth
      inputProps={inputProps}
      InputProps={InputProps}
      disabled={disabled}
      sx={{ 
        ...entityDrawerInputSx,
        gridColumn: fullWidth ? '1 / -1' : undefined,
        ...sx
      }}
    />
  )
}

interface EntityDrawerSelectProps {
  label: string
  required?: boolean
  value: string
  onChange: (value: string) => void
  options: EntityDrawerSelectOption[]
  placeholder?: string
  error?: string
  fullWidth?: boolean
  disabled?: boolean
}

export function EntityDrawerSelect({
  label,
  required,
  value,
  onChange,
  options,
  placeholder = 'Select',
  error,
  fullWidth,
  disabled,
}: EntityDrawerSelectProps) {
  // FormSelect (Autocomplete) needs the matching option object, not a plain string.
  // We find it here and extract the string back in onChange.
  const selectedOption = options.find(o => o.value === value) ?? null

  return (
    <FormSelect
      label={label}
      labelPosition="above"
      required={required}
      value={selectedOption}
      onChange={(v) => onChange((v as any)?.value ?? '')}
      options={options}
      error={!!error}
      helperText={error}
      disabled={disabled}
      placeholder={placeholder}
      fullWidth
      sx={{ 
        ...entityDrawerInputSx,
        gridColumn: fullWidth ? '1 / -1' : undefined 
      }}
    />
  )
}

/**
 * A generic field wrapper used when custom components (like Autocomplete or chips)
 * need to be rendered with a standardized drawer-style label.
 */
interface EntityFormFieldProps {
  label: string
  required?: boolean
  children: ReactNode
  fullWidth?: boolean
}

export function EntityFormField({
  label,
  required,
  children,
  fullWidth,
}: EntityFormFieldProps) {
  return (
    <Box sx={{ width: '100%', gridColumn: fullWidth ? '1 / -1' : undefined }}>
      <Typography
        component="label"
        sx={{
          display: 'block',
          fontSize: '12px',
          fontWeight: 600,
          color: themeColors.text.primary,
          mb: 0.5,
          userSelect: 'none',
        }}
      >
        {label}
        {required && (
          <Box component="span" sx={{ color: themeColors.error, ml: '2px' }}>
            *
          </Box>
        )}
      </Typography>
      {children}
    </Box>
  )
}
